import { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";

function relativeNotificationTime(value: Date | string) {
  const date = value instanceof Date ? value : new Date(value);
  const elapsedMinutes = Math.round((date.getTime() - Date.now()) / 60_000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  if (Math.abs(elapsedMinutes) < 60) return formatter.format(elapsedMinutes, "minute");
  const elapsedHours = Math.round(elapsedMinutes / 60);
  if (Math.abs(elapsedHours) < 24) return formatter.format(elapsedHours, "hour");
  return formatter.format(Math.round(elapsedHours / 24), "day");
}

function isSafeAppPath(value: string | null): value is string {
  return Boolean(value?.startsWith("/") && !value.startsWith("//"));
}

export default function NotificationBell({ admin = false }: { admin?: boolean }) {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn || !trpc.notifications?.inAppList?.useQuery) return null;
  return <ConnectedNotificationBell admin={admin} />;
}

function ConnectedNotificationBell({ admin }: { admin: boolean }) {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const utils = trpc.useUtils();
  const list = trpc.notifications.inAppList.useQuery(
    { limit: 30 },
    {
      enabled: true,
      retry: false,
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
      staleTime: 20_000,
    },
  );
  const markRead = trpc.notifications.markRead.useMutation({
    onSuccess: () => utils.notifications.inAppList.invalidate(),
  });
  const markAllRead = trpc.notifications.markAllRead.useMutation({
    onSuccess: () => utils.notifications.inAppList.invalidate(),
  });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const unreadCount = list.data?.unreadCount ?? 0;
  const items = list.data?.items ?? [];

  return (
    <div className="chi-notification-bell" ref={rootRef}>
      <button
        type="button"
        className="chi-notification-bell__trigger"
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : "Notifications"
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        data-admin={admin ? "true" : "false"}
      >
        <Bell size={19} aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="chi-notification-bell__count" aria-hidden="true">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <section
          className="chi-notification-panel"
          role="dialog"
          aria-label="Notifications"
        >
          <header className="chi-notification-panel__header">
            <div>
              <p>Notifications</p>
              <span aria-live="polite">
                {unreadCount === 0
                  ? "You’re all caught up"
                  : `${unreadCount} unread`}
              </span>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
              >
                {markAllRead.isPending
                  ? <Loader2 className="chi-notification-spinner" size={14} />
                  : <CheckCheck size={14} />}
                Mark all read
              </button>
            )}
          </header>

          <div className="chi-notification-panel__list">
            {list.isLoading ? (
              <div className="chi-notification-panel__state" role="status">
                <Loader2 className="chi-notification-spinner" size={22} />
                Loading notifications…
              </div>
            ) : list.error ? (
              <div className="chi-notification-panel__state" role="alert">
                <strong>Notifications are unavailable.</strong>
                <button type="button" onClick={() => list.refetch()}>
                  Try again
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="chi-notification-panel__state">
                <Bell size={24} aria-hidden="true" />
                <strong>No notifications yet</strong>
                <span>Important account and Community updates will appear here.</span>
              </div>
            ) : (
              items.map(item => {
                const isUnread = !item.readAt;
                const canOpen = isSafeAppPath(item.href);
                const destination = canOpen ? item.href : null;
                return (
                  <button
                    type="button"
                    className="chi-notification-item"
                    data-unread={isUnread ? "true" : "false"}
                    key={item.id}
                    onClick={() => {
                      if (isUnread) markRead.mutate({ notificationId: item.id });
                      if (destination) {
                        setOpen(false);
                        navigate(destination);
                      }
                    }}
                  >
                    <span className="chi-notification-item__dot" aria-hidden="true" />
                    <span className="chi-notification-item__copy">
                      <strong>{item.title}</strong>
                      <span>{item.body}</span>
                      <small>
                        {item.kind === "community" ? "Community" : "Account"}
                        {" · "}
                        {relativeNotificationTime(item.createdAt)}
                      </small>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </section>
      )}
    </div>
  );
}
